import React, { useState, useEffect } from 'react';
import { Table, Button, Select, Upload, message, Typography, Tooltip, Modal, Input, Tag } from 'antd';
import {
    UploadOutlined,
    InboxOutlined,
    LinkOutlined,
    ReloadOutlined,
    InfoCircleOutlined,
    CheckCircleFilled,
    ClockCircleFilled,
    CloseCircleFilled,
    CloudUploadOutlined,
    HistoryOutlined,
    SendOutlined,
    RobotOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { useAuthOrg } from '../context/AuthOrgContext';
import { useMetaSetup } from '../context/MetaSetupContext';
import coreAPIService from '../apiServices/coreAPIService';
import { CTW_API_ENDPOINT, CTW_NODE_API_ENDPOINT } from '../utils';
import CommonLayout from '../common/LayoutBlock';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import { useNavigate } from 'react-router-dom';
import { toast } from '../common/Popover';

const { Option } = Select;
const { Dragger } = Upload;
const { Text } = Typography;

// ─── Interfaces ──────────────────────────────────────────────────────────────

type MatchRatesValue = string | Record<string, unknown> | Record<string, unknown>[] | null;

interface UploadRecord {
    id: string | number;
    file_name?: string;
    fileName?: string;
    file_url?: string;
    fileUrl?: string;
    add_info?: string;
    unique_count?: number;
    uniqueCount?: number;
    already_processed?: number;
    alreadyProcessed?: number;
    capi_pushed?: number;
    capiPushed?: number;
    status?: string;
    campaign_match_rates?: MatchRatesValue;
    campaignMatchRates?: MatchRatesValue;
    created_at?: string;
    createdAt?: string;
}

type UploadStatus = 'success' | 'processing' | 'failed';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (iso: string) => {
    try {
        return new Date(iso).toLocaleString('en-IN', {
            dateStyle: 'medium',
            timeStyle: 'short',
        });
    } catch {
        return iso;
    }
};

const STATUS_CONFIG: Record<string, { icon: React.ReactNode; label: string; cls: string }> = {
    success: {
        icon: <CheckCircleFilled className="text-green-600" />,
        label: 'Success',
        cls: 'bg-green-50 text-green-700 border border-green-200',
    },
    completed: {
        icon: <CheckCircleFilled className="text-green-600" />,
        label: 'Completed',
        cls: 'bg-green-50 text-green-300 border border-green-200',
    },
    processing: {
        icon: <ClockCircleFilled className="text-blue-500" />,
        label: 'Processing',
        cls: 'bg-blue-50 text-blue-700 border border-blue-200',
    },
    failed: {
        icon: <CloseCircleFilled className="text-red-500" />,
        label: 'Failed',
        cls: 'bg-red-50 text-red-700 border border-red-200',
    },
};

const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
    console.log(status);

    const key = (status ?? '').toLowerCase().includes("processing") ? "processing" : (status ?? '').toLowerCase();
    const cfg = STATUS_CONFIG[key] ?? STATUS_CONFIG.processing;
    return status ? (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.cls}`}>
            {cfg.icon}
            {status ?? status ?? '—'}
        </span>
    ) : <Text type="secondary">—</Text>;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const parseMatchRates = (value?: MatchRatesValue) => {
    if (!value) return null;
    if (typeof value !== 'string') return value;

    const trimmed = value.trim();
    if (!trimmed) return null;

    try {
        const parsed = JSON.parse(trimmed);
        return isRecord(parsed) || Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const getMatchRateEntries = (matchRates: ReturnType<typeof parseMatchRates>) => {
    if (!matchRates) return [];
    return Array.isArray(matchRates)
        ? matchRates.flatMap((item) => (isRecord(item) ? Object.entries(item) : []))
        : Object.entries(matchRates);
};

const toFiniteNumber = (value: unknown) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const getCampaignMatchRates = (record: UploadRecord) =>
    record.campaignMatchRates ?? record.campaign_match_rates;

const getAlreadyProcessed = (record: UploadRecord) =>
    record.alreadyProcessed ?? record.already_processed;

const hasPushableMatchRates = (record: UploadRecord) => {
    const matchRates = parseMatchRates(getCampaignMatchRates(record));
    if (getMatchRateEntries(matchRates).length === 0) return false;

    const matchedNumbers = Array.isArray(matchRates)
        ? matchRates.find((item) => isRecord(item) && 'matchedNumbers' in item)?.matchedNumbers
        : matchRates?.matchedNumbers;
    const matchedCount = toFiniteNumber(matchedNumbers);
    const alreadyProcessed = toFiniteNumber(getAlreadyProcessed(record));

    return matchedCount === null || alreadyProcessed === null || matchedCount !== alreadyProcessed;
};

const canPushConversion = (record: UploadRecord) =>
    Boolean(!record.status?.toLowerCase().includes('complete')) && hasPushableMatchRates(record);

/** Render match-rate JSON text as coloured tags */
const MatchRateCell: React.FC<{ value?: MatchRatesValue }> = ({ value }) => {
    if (!value) return <Text type="secondary">—</Text>;
    const parsed = parseMatchRates(value);
    const entries = getMatchRateEntries(parsed);

    if (entries.length) {
        return (
            <div className="flex flex-wrap gap-1">
                {entries.map(([k, v], index) => (
                    <Tag key={`${k}-${index}`} color="blue" className="text-xs font-mono">
                        {k}: {String(v)}
                    </Tag>
                ))}
            </div>
        );
    }

    return parsed ? <Text type="secondary">—</Text> : <span className="text-xs text-gray-600 font-mono break-all">{String(value)}</span>;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const CardHeader: React.FC<{ icon: React.ReactNode; title: string; action?: React.ReactNode }> = ({
    icon, title, action,
}) => (
    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 text-[#1F344C] text-base">
                {icon}
            </div>
            <span className="text-sm font-semibold text-gray-800">{title}</span>
        </div>
        {action}
    </div>
);

const nowrapTitle = (title: string) => <span className="whitespace-nowrap">{title}</span>;

// ─── Component ───────────────────────────────────────────────────────────────

const OfflineStatusUpload: React.FC = () => {
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumbs();
    const { availableOrgs, currentOrg, user } = useAuthOrg();

    const { orgWabaData, fetchWabaDetails } = useMetaSetup();

    const [orgId, setOrgId] = useState<string>(currentOrg?.orgId || '');
    const [botId, setBotId] = useState<string>('');
    const [bots, setBots] = useState<{ botId: string; botName: string; status: boolean }[]>([]);
    const [botsLoading, setBotsLoading] = useState(false);
    const [fileList, setFileList] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);
    const [records, setRecords] = useState<UploadRecord[]>([]);
    const [loadingRecords, setLoadingRecords] = useState(false);
    const [pushingId, setPushingId] = useState<string | number | null>(null);

    useEffect(() => {
        setBreadcrumbs([
            {
                title: (
                    <div className="text-lg cursor-pointer" onClick={() => navigate('/settings')}>
                        Settings
                    </div>
                ),
            },
            { title: <div className="text-lg">Offline Status Upload</div> },
        ]);
        if (orgWabaData.length === 0) fetchWabaDetails();
    }, [setBreadcrumbs]);

    // Fetch bots whenever orgId changes
    useEffect(() => {
        if (!orgId) { setBots([]); setBotId(''); return; }
        const orgData = orgWabaData.find(o => o.orgId === orgId);
        if (!orgData || orgData.wabaDetails.length === 0) { setBots([]); setBotId(''); return; }

        const fetchBots = async () => {
            setBotsLoading(true);
            setBotId('');
            try {
                const results = await Promise.all(
                    orgData.wabaDetails.map(w =>
                        coreAPIService.get<any>(CTW_API_ENDPOINT, `v2/org/clients/${w.clientId}/bots`)
                    )
                );
                const allBots = results.flatMap(res =>
                    res?.meta?.code === 200 && Array.isArray(res.data) ? res.data : []
                );
                // dedupe by botId
                const seen = new Set<string>();
                const unique = allBots.filter((b: any) => {
                    if (seen.has(b.botId)) return false;
                    seen.add(b.botId);
                    return true;
                });
                setBots(unique);
                // auto-select first live bot
                const live = unique.find((b: any) => b.status);
                if (live) setBotId(live.botId);
            } catch {
                setBots([]);
            } finally {
                setBotsLoading(false);
            }
        };
        fetchBots();
    }, [orgId, orgWabaData]);

    const fetchHistory = async (selectedOrgId?: string) => {
        const id = selectedOrgId || orgId;
        if (!id) return;
        setLoadingRecords(true);
        try {
            const res = await coreAPIService.get<any>(
                CTW_NODE_API_ENDPOINT,
                `common/v1/list?orgId=${id}`
            );
            setRecords(res?.data || []);
        } catch (err) {
            console.error('Failed to fetch upload history', err);
            setRecords([]);
        } finally {
            setLoadingRecords(false);
        }
    };

    useEffect(() => {
        if (orgId) fetchHistory(orgId);
    }, [orgId]);

    const handleUpload = async () => {
        if (!orgId) { message.warning('Please select an account first.'); return; }
        if (!botId) { message.warning('Please select a Bot first.'); return; }
        if (!user?.userId) { message.error('User not found. Please reload.'); return; }
        if (fileList.length === 0) { message.warning('Please select a file to upload.'); return; }

        const file = fileList[0] as File;

        if (file.name.endsWith('.csv')) {
            const text = await file.text();
            const firstLine = text.split('\n')[0].toLowerCase().replace(/\r/g, '');
            const headers = firstLine.split(',').map((h) => h.trim());
            if (!headers.includes('mobile') || !headers.includes('status')) {
                message.error('CSV must have "mobile" and "status" columns as headers.');
                return;
            }
        }

        setUploading(true);
        try {
            // const formDataNode = new FormData();
            // formDataNode.append('file', file);
            // formDataNode.append('org_id', orgId);
            // formDataNode.append('created_by', String(user.userId));
            // formDataNode.append('campaign_id', botId.trim());

            // await coreAPIService.post<any>(
            //     CTW_NODE_API_ENDPOINT,
            //     `common/v1/upload`,
            //     formDataNode,
            //     {
            //         headers: {
            //             'Content-Type': 'multipart/form-data',
            //         },
            //     },
            // );

            const formData = new FormData();
            formData.append('file', file);

            const res = await coreAPIService.post<any>(
                CTW_API_ENDPOINT,
                `v2/offline-capi/upload?orgId=${orgId}&botId=${encodeURIComponent(botId.trim())}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            );

            if (res?.meta?.code === 200 || res?.meta?.status || res?.data) {
                message.success('File uploaded successfully!');
                fetchHistory(orgId);
            } else {
                throw new Error(res?.meta?.message || 'Upload failed');
            }
        } catch (err: any) {
            message.error(
                err?.response?.data?.meta?.message || err?.message || 'Failed to upload file.'
            );
        } finally {
            setUploading(false);
            setFileList([]);
        }
    };

    const handlePushConversion = async (record: UploadRecord) => {
        if (!canPushConversion(record)) {
            message.warning('No conversion action is available for this upload.');
            return;
        }

        const fileUploadId = record.id;
        setPushingId(fileUploadId);
        try {
            await coreAPIService.post<any>(
                CTW_API_ENDPOINT,
                `v2/offline-capi/execute-push?fileUploadId=${fileUploadId}&orgId=${orgId}`,
                {},
            );
            message.success('Conversion push initiated successfully!');
            fetchHistory(orgId);
        } catch (err: any) {
            message.error(
                err?.response?.data?.meta?.message || err?.message || 'Failed to push conversion.'
            );
        } finally {
            setPushingId(null);
        }
    };

    const draggerProps: UploadProps = {
        name: 'file',
        multiple: false,
        accept: '.csv,.xlsx,.xls',
        fileList: fileList as any,
        beforeUpload: (file) => { setFileList([file as any]); return false; },
        onRemove: () => setFileList([]),
    };

    const columns = [
        {
            title: 'File Name',
            key: 'fileName',
            width: 120,
            render: (_: any, r: UploadRecord) => {
                const name = r.file_name || r.fileName;
                return (
                    <div className="flex items-center gap-2 min-w-[120px]">
                        <span className="flex items-center justify-center w-7 h-7 rounded-md bg-slate-100 text-sm flex-shrink-0">
                            📄
                        </span>
                        <Text className="max-w-[120px] text-sm font-medium text-gray-800 " ellipsis={{ tooltip: name }}>
                            {name || '—'}
                        </Text>
                    </div>
                );
            },
        },
        // {
        //     title: 'Source File',
        //     key: 'fileUrl',
        //     render: (_: any, r: UploadRecord) => {
        //         const url = r.file_url || r.fileUrl;
        //         return url ? (
        //             <a
        //                 href={url}
        //                 target="_blank"
        //                 rel="noopener noreferrer"
        //                 className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800 transition-colors no-underline"
        //             >
        //                 <LinkOutlined />
        //                 View File
        //             </a>
        //         ) : (
        //             <Text type="secondary">—</Text>
        //         );
        //     },
        // },
        {
            title: nowrapTitle('Unique Records'),
            dataIndex: 'uniqueCount',
            key: 'uniqueCount',
            align: 'right' as const,
            width: 140,
            className: 'whitespace-nowrap',
            render: (val: number) => (
                <span className="text-sm font-medium text-gray-700 tabular-nums whitespace-nowrap">
                    {val?.toLocaleString() ?? '—'}
                </span>
            ),
        },
        {
            title: nowrapTitle('Already Processed'),
            dataIndex: 'alreadyProcessed',
            key: 'alreadyProcessed',
            align: 'right' as const,
            width: 160,
            className: 'whitespace-nowrap',
            render: (val: number) => (
                <span className="text-sm font-medium text-amber-600 tabular-nums whitespace-nowrap">
                    {val?.toLocaleString() ?? '—'}
                </span>
            ),
        },
        {
            title: nowrapTitle('CAPI Pushed'),
            dataIndex: 'capiPushed',
            key: 'capiPushed',
            align: 'right' as const,
            width: 130,
            className: 'whitespace-nowrap',
            render: (val: number) => (
                <span className="text-sm font-semibold text-[#1F344C] tabular-nums whitespace-nowrap">
                    {val?.toLocaleString() ?? '—'}
                </span>
            ),
        },
        {
            title: nowrapTitle('Match Rates'),
            width: 260,
            dataIndex: 'campaignMatchRates',
            key: 'campaignMatchRates',
            render: (_: MatchRatesValue, record: UploadRecord) => (
                <MatchRateCell value={getCampaignMatchRates(record)} />
            ),
        },
        {
            title: nowrapTitle('Status'),
            dataIndex: 'status',
            width: 140,
            key: 'status',
            className: 'whitespace-nowrap',
            render: (status: string, record: UploadRecord) => status?.toLowerCase().includes('complete') ? <StatusBadge status={status} /> : canPushConversion(record) ? <StatusBadge status={status} /> : "-",
        },
        {
            title: nowrapTitle('Uploaded At'),
            key: 'createdAt',
            width: 180,
            className: 'whitespace-nowrap',
            render: (_: any, r: UploadRecord) => {
                const date = r.created_at || r.createdAt;
                return (
                    <span className="text-xs text-gray-400 whitespace-nowrap">{date ? formatDate(date) : '—'}</span>
                );
            },
        },
        {
            title: nowrapTitle('Action'),
            key: 'action',
            width: 170,
            className: 'whitespace-nowrap',
            render: (_: any, record: UploadRecord) => (
                canPushConversion(record) ? <Button
                    type="primary"
                    size="small"
                    icon={<SendOutlined />}
                    loading={pushingId === record.id}
                    onClick={() =>
                        Modal.confirm({
                            title: 'Push Conversion',
                            content: `Are you sure you want to push conversions for this upload (ID: ${record.id})?`,
                            okText: 'Push',
                            cancelText: 'Cancel',
                            onOk: () => handlePushConversion(record),
                        })
                    }
                    className="!bg-[#1F344C] !border-[#1F344C] !rounded-md !text-xs !font-semibold !whitespace-nowrap hover:!opacity-90"
                >
                    Push Conversion
                </Button> : <Text type="secondary">—</Text>
            ),
        },
    ];

    return (
        <CommonLayout>
            <div className="p-8 min-h-screen bg-gray-50">

                {/* ── Page Header ──────────────────────────────────── */}
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-gray-900 mb-1">
                        Offline Status Upload
                    </h1>
                    <p className="text-sm text-gray-500 max-w-xl">
                        Upload a CSV or spreadsheet with WhatsApp mobile numbers and verification
                        statuses to push conversion events to Meta CAPI.
                    </p>
                </div>

                {/* ── Upload Card ───────────────────────────────────── */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-5">
                    <CardHeader icon={<CloudUploadOutlined />} title="Upload Status File" />

                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-start">

                            {/* Account + Bot ID selectors */}
                            <div className="md:col-span-1 flex flex-col gap-4">
                                {/* Account selector */}
                                <div>
                                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                                        Account
                                    </label>
                                    <Select
                                        placeholder="Select account…"
                                        value={orgId || undefined}
                                        onChange={(val) => setOrgId(val)}
                                        className="w-full"
                                        showSearch
                                        filterOption={(input, option) =>
                                            String(option?.children ?? '')
                                                .toLowerCase()
                                                .includes(input.toLowerCase())
                                        }
                                    >
                                        {availableOrgs.map((org) => (
                                            <Option key={org.orgId} value={org.orgId}>
                                                <Tooltip title={org.orgName}>{org.orgName}</Tooltip>
                                            </Option>
                                        ))}
                                    </Select>
                                </div>

                                {/* Bot ID dropdown */}
                                <div>
                                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                                        Bot
                                    </label>
                                    <Select
                                        placeholder="Select Bot…"
                                        value={botId || undefined}
                                        onChange={(val) => setBotId(val)}
                                        disabled={!orgId}
                                        loading={botsLoading}
                                        className="w-full"
                                        showSearch
                                        optionFilterProp="label"
                                        allowClear
                                        onClear={() => setBotId('')}
                                        notFoundContent={botsLoading ? 'Loading…' : 'No bots found'}
                                    >
                                        {bots.map((bot) => (
                                            <Option key={bot.botId} value={bot.botId} label={bot.botName}>
                                                <div className="flex items-center gap-2">
                                                    <RobotOutlined className="text-gray-400" />
                                                    <span>{bot.botName}</span>
                                                    {bot.status && (
                                                        <span className="ml-auto text-xs text-green-600 font-medium">Live</span>
                                                    )}
                                                </div>
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                            </div>

                            {/* Drop zone */}
                            <div className="md:col-span-3">
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
                                    File — CSV / XLSX / XLS
                                </label>
                                <Dragger {...draggerProps}>
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined className="!text-[#1F344C] !text-4xl" />
                                    </p>
                                    <p className="ant-upload-text !text-gray-700 !font-medium">
                                        Drag a file here, or click to browse
                                    </p>
                                    <p className="ant-upload-hint !text-gray-400 !text-xs">
                                        Required columns:{' '}
                                        <code className="bg-gray-100 text-gray-600 rounded px-1 py-0.5 text-xs font-mono">mobile</code>
                                        {' '}and{' '}
                                        <code className="bg-gray-100 text-gray-600 rounded px-1 py-0.5 text-xs font-mono">status</code>
                                    </p>
                                </Dragger>
                            </div>
                        </div>

                        {/* Info banner */}
                        <div className="mt-5 flex items-center gap-2.5 px-4 py-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-700">
                            <InfoCircleOutlined className="text-blue-400 flex-shrink-0" />
                            <span>
                                Accepted values for{' '}
                                <code className="bg-blue-100 text-blue-800 rounded px-1.5 py-0.5 text-xs font-mono">status</code>
                                {' '}column:{' '}
                                <span className="inline-flex items-center mx-1 px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold">
                                    QualifiedLead
                                </span>
                                <span className="inline-flex items-center mx-1 px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold">
                                    Purchase
                                </span>
                            </span>
                        </div>

                        {/* Submit */}
                        <div className="mt-5 pt-5 border-t border-gray-100 flex justify-end">
                            <Button
                                type="primary"
                                icon={<UploadOutlined />}
                                onClick={() =>
                                    Modal.confirm({
                                        title: 'Upload File',
                                        content: 'Are you sure you want to upload this file?',
                                        okText: 'Upload',
                                        cancelText: 'Cancel',
                                        onOk: async () => {
                                            if (!orgId) { message.warning('Please select an account first.'); return; }
                                            if (!botId) { message.warning('Please select a Bot first.'); return; }
                                            if (fileList.length === 0) { message.warning('Please select a file to upload.'); return; }
                                            handleUpload();
                                        },
                                    })
                                }
                                loading={uploading}
                                disabled={fileList.length === 0 || !orgId || !botId}
                                size="large"
                                className="!bg-[#1F344C] !border-[#1F344C] !rounded-lg !font-semibold hover:!opacity-90 disabled:!bg-gray-300 disabled:!border-gray-300"
                            >
                                Upload & Push to CAPI
                            </Button>
                        </div>
                    </div>
                </div>

                {/* ── History Card ──────────────────────────────────── */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                    <CardHeader
                        icon={<HistoryOutlined />}
                        title="Upload History"
                        action={
                            <Button
                                icon={<ReloadOutlined />}
                                size="small"
                                onClick={() => fetchHistory(orgId)}
                                loading={loadingRecords}
                                disabled={!orgId}
                                className="!rounded-md !text-xs !text-gray-500 !border-gray-200 hover:!text-[#1F344C] hover:!border-[#1F344C]"
                            >
                                Refresh
                            </Button>
                        }
                    />

                    <Table
                        columns={columns}
                        dataSource={records}
                        rowKey={(r) => String(r.id ?? r.createdAt ?? r.created_at ?? Math.random())}
                        loading={loadingRecords}
                        pagination={{
                            pageSize: 10,
                            size: 'small',
                            showTotal: (t) => `${t} uploads`,
                        }}
                        scroll={{ x: 'max-content' }}
                        className="overflow-auto"
                        locale={{
                            emptyText: (
                                <div className="py-12 text-center">
                                    <p className="text-gray-400 text-sm">
                                        {orgId
                                            ? 'No uploads found for this account.'
                                            : 'Select an account to view upload history.'}
                                    </p>
                                </div>
                            ),
                        }}
                    />
                </div>

            </div>
        </CommonLayout>
    );
};

export default OfflineStatusUpload;
